import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { readStoredJson } from "../../utilities/session";
import { toast } from "react-toastify";
import { Button, Card, Alert, Table, Spinner, Form, InputGroup } from "react-bootstrap";

const MIN_WITHDRAW_POINTS = 1000;

const WalletPage = () => {
  const user = readStoredJson("user", {});
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const walletBalance = Number(wallet?.balance ?? 0);

  const userId = user?.id;

  const activeRole = user?.roles?.find(
    (r) => r.name?.toLowerCase() !== 'user'
  ) ?? user?.roles?.[0];

  // Reason: the wallet API ignores the role segment, but the URL still requires one.
  const roleId = activeRole?.pivot?.role_id ?? activeRole?.id ?? 0;

  useEffect(() => {
    if (!userId) {
      setError("You need to log in again to view your wallet.");
      setLoading(false);
      return;
    }

    const fetchWallet = async () => {
      try {
        const res = await api.get(`/api/wallet/${userId}/${roleId}`);
        setWallet(res.data.wallet);
        setLoading(false);
      } catch (error) {
        setError("Failed to fetch wallet data.");
        setLoading(false);
      }
    };

    fetchWallet();
  }, [userId, roleId]);

  const getAmountError = (value) => {
    const points = Number(value);
    if (value === "" || Number.isNaN(points)) {
      return "Enter the number of points to withdraw.";
    }
    if (points < MIN_WITHDRAW_POINTS) {
      return `You can't withdraw less than ${MIN_WITHDRAW_POINTS} points.`;
    }
    if (points > walletBalance) {
      return "You can't withdraw more than your balance.";
    }
    if (!/^\d+(\.\d{1,2})?$/.test(String(value))) {
      return "Use at most two decimal places.";
    }
    return "";
  };

  const handleWithdraw = (e) => {
    e.preventDefault();
    const amountError = getAmountError(amount);
    if (amountError) {
      setError(amountError);
      return;
    }

    setError("");
    setWithdrawing(true);
    api
      .post(`/api/wallet/${userId}/${roleId}/withdraw`, {
        amount: Number(amount),
      })
      .then((res) => {
        toast.success("Withdraw successful!");
        setWallet(res.data.wallet);
        setAmount("");
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Withdraw failed!");
      })
      .finally(() => setWithdrawing(false));
  };

  const handleGeneratePDF = async () => {
    if (!wallet) return;

    try {
      const res = await api.get(`/api/wallet/pdf/${userId}/${roleId}`, {
        responseType: "blob", // crucial for PDF
      });

      // create file link
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `withdraw_slip_user_${userId}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      // Laravel  error message
      toast.error("Unable to download PDF.");
    }
  };

  if (loading) {
    return (
      <>
        <div className="text-center mt-5">
          <Spinner animation="border" />
        </div>
      </>
    );
  }

if (!wallet) {
  return (
    <Alert variant="danger" className="mt-5 text-center">
      Wallet data not found
    </Alert>
  );
}
  return (
    <>
      <div className="container-fluid">
        <Card className="shadow-lg p-4">
          <h3 className="text-center text-primary mb-4">
            {wallet?.user?.name}'s Wallet
          </h3>

          {error && <Alert variant="danger">{error}</Alert>}

          <Table bordered hover className="text-center">
            <thead className="table-primary">
              <tr>
                <th>User Name</th>
                <th>Designation </th>
                <th>Mobile </th>
                <th>Balance (Points)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{wallet?.user?.name}</td>
                <td>{activeRole?.name ?? '—'}</td>
                <td>{wallet?.user?.mobile_number}</td>
                <td className="fw-bold text-success fs-4">
                  {wallet?.balance} pts
                </td>
              </tr>
            </tbody>
          </Table>

          <Form
            noValidate
            onSubmit={handleWithdraw}
            className="d-flex flex-wrap justify-content-center align-items-start gap-3 mt-4"
          >
            <Form.Group controlId="withdrawAmount" style={{ minWidth: "260px" }}>
              <InputGroup>
                <Form.Control
                  type="number"
                  min={MIN_WITHDRAW_POINTS}
                  max={walletBalance}
                  step="0.01"
                  placeholder={`Points (min ${MIN_WITHDRAW_POINTS})`}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={walletBalance < MIN_WITHDRAW_POINTS || withdrawing}
                />
                <InputGroup.Text>pts</InputGroup.Text>
              </InputGroup>
              {walletBalance >= MIN_WITHDRAW_POINTS && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mt-1"
                  onClick={() => setAmount(String(walletBalance))}
                >
                  Use full balance
                </Button>
              )}
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              disabled={walletBalance < MIN_WITHDRAW_POINTS || withdrawing}
            >
              {withdrawing ? "Withdrawing..." : "Withdraw"}
            </Button>

            <Button variant="secondary" onClick={handleGeneratePDF}>
              Download Withdraw Slip
            </Button>
          </Form>

          {walletBalance < MIN_WITHDRAW_POINTS && (
            <p className="text-danger text-center mt-3 fw-bold">
              You need at least {MIN_WITHDRAW_POINTS} points to withdraw.
            </p>
          )}
        </Card>
      </div>
    </>
  );
};

export default WalletPage;
